/**
 * Master SEO Controller
 * Sistema unificado de gestión SEO que coordina todos los componentes SEO
 * Evita duplicación, optimiza carga y prioriza componentes críticos
 */

import React, { lazy, Suspense } from 'react';
import { generateMetaTags } from '@/utils/seo/metaTagsGenerator';
import SafeSEOComponent from './SafeSEOComponent';
import EnhancedSEOSystem from './EnhancedSEOSystem';
import AdvancedStructuredData from './AdvancedStructuredData';
import CoreWebVitalsOptimizer from './CoreWebVitalsOptimizer';
import MultilingualSEO from './MultilingualSEO';
import TrustSignals from './TrustSignals';

// Lazy load non-critical SEO components
const GeoTargetingSEO = lazy(() => import('./GeoTargetingSEO'));
const URLOptimization = lazy(() => import('./URLOptimization'));
const EnhancedFAQSchema = lazy(() => import('./EnhancedFAQSchema'));
const InternalLinking = lazy(() => import('./InternalLinking'));
const LocalContentHubs = lazy(() => import('./LocalContentHubs'));

interface MasterSEOControllerProps {
  pageType: 'home' | 'city' | 'country' | 'specialty' | 'therapist' | 'find' | 'about' | 'contact' | 'pricing' | 'static';
  entityData?: any;
  currentLanguage?: string;
  customMetaTags?: {
    title?: string;
    description?: string;
    keywords?: string[];
  };
  enabledFeatures?: {
    geoTargeting?: boolean;
    multilingual?: boolean;
    faqSchema?: boolean;
    internalLinking?: boolean;
    localContentHubs?: boolean;
    trustSignals?: boolean;
  };
  ratings?: {
    averageRating: number;
    reviewCount: number;
  };
  businessInfo?: {
    phone?: string;
    email?: string;
    address?: any;
  };
}

/**
 * Determina qué componentes SEO se deben cargar según el tipo de página
 */
const getSEOComponentConfig = (pageType: string) => {
  const configs = {
    home: {
      critical: ['meta', 'structured', 'performance', 'multilingual'],
      important: ['faq', 'trust'],
      optional: ['internal-linking']
    },
    city: {
      critical: ['meta', 'structured', 'performance', 'geo', 'multilingual'],
      important: ['faq', 'trust', 'url'],
      optional: ['internal-linking', 'local-hubs']
    },
    country: {
      critical: ['meta', 'structured', 'performance', 'multilingual'],
      important: ['faq', 'trust', 'url'],
      optional: ['internal-linking']
    },
    specialty: {
      critical: ['meta', 'structured', 'performance'],
      important: ['faq', 'trust'],
      optional: ['internal-linking']
    },
    find: {
      critical: ['meta', 'structured', 'performance'],
      important: ['faq'],
      optional: []
    },
    static: {
      critical: ['meta', 'performance'],
      important: [],
      optional: []
    }
  };
  
  return configs[pageType as keyof typeof configs] || configs.static;
};

const MasterSEOController: React.FC<MasterSEOControllerProps> = ({
  pageType,
  entityData,
  currentLanguage = 'en',
  customMetaTags,
  enabledFeatures = {},
  ratings,
  businessInfo
}) => {
  // Generar meta tags optimizados
  const metaTags = generateMetaTags({
    pageType,
    entityData,
    customTitle: customMetaTags?.title,
    customDescription: customMetaTags?.description,
    customKeywords: customMetaTags?.keywords
  });
  
  // Preparar datos SEO para EnhancedSEOSystem
  const seoData = {
    title: metaTags.title,
    description: metaTags.description,
    keywords: metaTags.keywords,
    h1Title: metaTags.h1,
    canonicalUrl: metaTags.canonicalUrl,
    ogImage: metaTags.ogImage,
    ogType: (metaTags.ogType === 'article' ? 'article' : 'website') as 'article' | 'website'
  };
  
  // Mapear pageType a los tipos esperados por cada componente
  const enhancedSEOPageType = ['city', 'country', 'home', 'therapist', 'search', 'static'].includes(pageType) 
    ? pageType as 'city' | 'country' | 'home' | 'therapist' | 'search' | 'static'
    : 'static';
    
  const structuredDataPageType = ['city', 'country', 'home', 'therapist', 'article'].includes(pageType)
    ? pageType as 'city' | 'country' | 'home' | 'therapist' | 'article'
    : 'home';
    
  const multilingualPageType = ['city', 'country', 'home', 'therapist'].includes(pageType)
    ? pageType as 'city' | 'country' | 'home' | 'therapist'
    : 'home';
    
  const geoTargetingPageType = ['city', 'country', 'region'].includes(pageType)
    ? pageType as 'city' | 'country' | 'region'
    : 'city';
    
  const urlOptimizationPageType = ['city', 'country', 'home', 'therapist', 'search'].includes(pageType)
    ? pageType as 'city' | 'country' | 'home' | 'therapist' | 'search'
    : (pageType === 'find' ? 'search' : 'home');
    
  const faqSchemaPageType = ['city', 'home', 'therapist', 'article'].includes(pageType)
    ? pageType as 'city' | 'home' | 'therapist' | 'article'
    : 'home';
    
  const trustSignalsPageType = ['city', 'home', 'therapist', 'article'].includes(pageType)
    ? pageType as 'city' | 'home' | 'therapist' | 'article'
    : 'home';
    
  const internalLinkingPageType = ['city', 'country', 'home', 'therapist'].includes(pageType)
    ? pageType as 'city' | 'country' | 'home' | 'therapist'
    : 'home';
  
  // Obtener configuración de componentes para este tipo de página
  const componentConfig = getSEOComponentConfig(pageType);
  
  // Determinar features habilitadas (por defecto todas activas)
  const features = {
    geoTargeting: enabledFeatures.geoTargeting !== false,
    multilingual: enabledFeatures.multilingual !== false,
    faqSchema: enabledFeatures.faqSchema !== false,
    internalLinking: enabledFeatures.internalLinking !== false,
    localContentHubs: enabledFeatures.localContentHubs !== false,
    trustSignals: enabledFeatures.trustSignals !== false
  };
  
  return (
    <>
      {/* CRITICAL: Meta tags básicos - Siempre se cargan */}
      {componentConfig.critical.includes('meta') && (
        <EnhancedSEOSystem 
          pageData={seoData}
          pageType={enhancedSEOPageType}
          entityData={entityData}
        />
      )}
      
      {/* CRITICAL: Structured Data - Esencial para SEO */}
      {componentConfig.critical.includes('structured') && (
        <AdvancedStructuredData
          pageType={structuredDataPageType}
          entityData={entityData}
          ratings={ratings}
          contactInfo={businessInfo}
        />
      )}
      
      {/* CRITICAL: Core Web Vitals - Performance */}
      {componentConfig.critical.includes('performance') && (
        <CoreWebVitalsOptimizer 
          pageType={pageType}
          entityData={entityData}
          optimizations={{
            preloadCriticalResources: true,
            optimizeImages: true,
            prefetchRoutes: pageType === 'city' || pageType === 'country',
            enableResourceHints: true
          }}
        />
      )}
      
      {/* CRITICAL: Multilingual SEO */}
      {componentConfig.critical.includes('multilingual') && features.multilingual && (
        <MultilingualSEO 
          pageType={multilingualPageType}
          entityData={entityData}
          currentLanguage={currentLanguage}
          translatedContent={{
            [currentLanguage]: {
              title: metaTags.title,
              description: metaTags.description,
              keywords: metaTags.keywords.split(', '),
              h1: metaTags.h1
            }
          }}
        />
      )}
      
      {/* Lazy load: Componentes importantes pero no críticos */}
      <Suspense fallback={null}>
        {/* Geo-targeting para páginas de ciudad */}
        {componentConfig.critical.includes('geo') && features.geoTargeting && entityData && (
          <SafeSEOComponent componentName="GeoTargetingSEO">
            <GeoTargetingSEO 
              pageType={geoTargetingPageType}
              entityData={entityData}
              targetAudience={{
                primaryCountry: entityData.country,
                primaryLanguage: currentLanguage,
                timeZone: entityData.timezone || 'UTC',
                currency: 'USD'
              }}
              localBusinessInfo={{
                businessName: `Mental Health Services in ${entityData.name}`,
                address: {
                  city: entityData.name,
                  country: entityData.country
                },
                phone: businessInfo?.phone || "+1-800-THERAPY",
                email: businessInfo?.email || "support@psychotherapist.help",
                serviceArea: [entityData.name, entityData.country].filter(Boolean)
              }}
            />
          </SafeSEOComponent>
        )}
        
        {/* URL Optimization */}
        {componentConfig.important.includes('url') && (
          <SafeSEOComponent componentName="URLOptimization">
            <URLOptimization
              pageType={urlOptimizationPageType}
              entityData={entityData}
              robotsDirectives={['imageindex', 'snippet', 'archive']}
            />
          </SafeSEOComponent>
        )}
        
        {/* FAQ Schema */}
        {componentConfig.important.includes('faq') && features.faqSchema && (
          <SafeSEOComponent componentName="EnhancedFAQSchema">
            <EnhancedFAQSchema
              pageType={faqSchemaPageType}
              entityData={entityData}
              showSearch={false}
              showCategories={false}
              maxVisible={6}
              visualDisplay={false}
            />
          </SafeSEOComponent>
        )}
        
        {/* Trust Signals */}
        {componentConfig.important.includes('trust') && features.trustSignals && (
          <SafeSEOComponent componentName="TrustSignals">
            <TrustSignals
              pageType={trustSignalsPageType}
              entityData={entityData}
              showRatings={!!ratings}
              showSecurity={pageType === 'home' || pageType === 'about'}
              showAccreditations={pageType === 'home' || pageType === 'about'}
              showStatistics={true}
            />
          </SafeSEOComponent>
        )}
        
        {/* Internal Linking - Solo en páginas relevantes */}
        {componentConfig.optional.includes('internal-linking') && features.internalLinking && (
          <SafeSEOComponent componentName="InternalLinking">
            <InternalLinking 
              pageType={internalLinkingPageType}
              entityData={entityData}
              maxLinks={pageType === 'city' ? 6 : 4}
              className="container mx-auto px-4 my-8"
            />
          </SafeSEOComponent>
        )}
        
        {/* Local Content Hubs - Solo para ciudades */}
        {componentConfig.optional.includes('local-hubs') && features.localContentHubs && pageType === 'city' && (
          <SafeSEOComponent componentName="LocalContentHubs">
            <LocalContentHubs 
              pageType={pageType}
              entityData={entityData}
            />
          </SafeSEOComponent>
        )}
      </Suspense>
    </>
  );
};

export default MasterSEOController;

